/**
 * WorkflowGuidanceCard — Pre-Live Polish P-5 (Feb 2026)
 * ---------------------------------------------------------------------------
 * Reusable, dismissible "How this works" card. One per page.
 * Each card has its own stable `storageKey` so dismissals are independent
 * across pages.
 *
 * Display contract:
 *   - Renders a soft-toned card with a title, a numbered ordered list of steps,
 *     and a "Got it" dismiss button.
 *   - On dismiss, sets `localStorage.{storageKey} = "1"` and unmounts.
 *   - On next mount, reads the same key — if set, renders null.
 *
 * Pure UI. No backend. No API calls.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Lightbulb, X } from 'lucide-react';

export function WorkflowGuidanceCard({ storageKey, title, steps }) {
  const [hidden, setHidden] = useState(true);

  useEffect(() => {
    try {
      const v = localStorage.getItem(storageKey);
      setHidden(v === '1');
    } catch {
      setHidden(false);
    }
  }, [storageKey]);

  if (hidden) return null;

  const dismiss = () => {
    try { localStorage.setItem(storageKey, '1'); } catch { /* ignore */ }
    setHidden(true);
  };

  return (
    <Card
      className="border-sky-200 bg-sky-50/60"
      data-testid={`workflow-guidance-${storageKey}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <Lightbulb className="h-4 w-4 mt-0.5 text-sky-700 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm text-sky-900" data-testid="workflow-guidance-title">
              {title}
            </div>
            <ol className="text-sm text-sky-900/90 mt-2 space-y-1 list-decimal list-inside">
              {steps.map((step, i) => (
                <li key={i} data-testid={`workflow-guidance-step-${i + 1}`}>{step}</li>
              ))}
            </ol>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={dismiss}
            className="h-7 text-xs text-sky-800"
            data-testid="workflow-guidance-dismiss"
            aria-label="Dismiss guidance"
          >
            <X className="h-3 w-3 mr-1" /> Got it
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default WorkflowGuidanceCard;
